import { Route, RouteConfig } from 'vue-router';
import { AAnalyticsService } from '@uikit/analytics-manager';
import { Router } from '@/router';
import { Store } from '@/store';
import { I18n, setupLocale, getLocaleName } from '@/i18n';
import { setupTheme } from '@/branding';
import { Branding } from '@/types/Branding';
import { UserPermissions, UserPermissionsService, createUserPermissionsService } from '@/app/services/UserPermissionsService';
import { Entry } from '@/types/Entry';
import { StoreModule, createStoreModule } from './store-module';
import { PricingService, createPricingService } from './services/PricingService';
import { SettingService, createSettingService } from './services/SettingService';
import { SelfServiceModeService, createSelfServiceModeService } from './services/SelfServiceModeService';
import { CompanyService, createCompanyService } from './services/CompanyService';
import { ContactService, createContactService } from './services/ContactService';
import { ConsumerProfileService, createConsumerProfileService } from './services/ConsumerProfileService';
import { DeleteAccountService, createDeleteAccountService } from './services/DeleteAccountService';

import messages from './locales/en-US.json';
import { routes } from './routes';

export type SyncProps = {
  selfTenantUuid: string;
  tenantUuid: string;
  language: string;
  branding: Branding;
};
export type InitializationData = SyncProps & { entry: Entry };

export class App {
  constructor(
    private router: Router,
    private store: Store,
    private i18n: I18n,
    private analytics: AAnalyticsService,
    private element?: HTMLElement
  ) {

  }

  public async initialize(data: InitializationData): Promise<void> {
    this.addRoutesToRouter();
    this.addHooksToRouter();
    await this.registerStoreModule(data.selfTenantUuid, data.tenantUuid);
    await this.setLanguage(data.language);
    await this.setBranding(data.branding);
    await this.setEntry(data.entry);
  }

  public async update(syncProps: SyncProps): Promise<void> {
    this.unregisterStoreModule();
    await this.registerStoreModule(syncProps.selfTenantUuid, syncProps.tenantUuid);
    await this.setLanguage(syncProps.language);
    await this.setBranding(syncProps.branding);
  }

  private addRoutesToRouter(): void {
    routes.forEach((route: RouteConfig) => this.router.addRoute(route));
  }

  private addHooksToRouter(): void {
    this.router.afterEach((to: Route) => {
      this.analytics?.sendPageView({
        title: to.name || process.env.VUE_APP_NAME,
        page: `${process.env.VUE_APP_PUBLIC_PATH_PREFIX}${process.env.VUE_APP_NAME}${to.path}`
      });
    });
  }

  private async registerStoreModule(selfTenantUuid: string, tenantUuid: string): Promise<void> {
    const pricingService: PricingService = createPricingService(selfTenantUuid, tenantUuid);
    const settingService: SettingService = createSettingService(selfTenantUuid, tenantUuid);
    const selfServiceModeService: SelfServiceModeService = createSelfServiceModeService(selfTenantUuid, tenantUuid);
    const companyService: CompanyService = createCompanyService(selfTenantUuid, tenantUuid);
    const contactService: ContactService = createContactService(selfTenantUuid, tenantUuid);
    const consumerProfileService: ConsumerProfileService = createConsumerProfileService(selfTenantUuid);
    const deleteAccountService: DeleteAccountService = createDeleteAccountService(selfTenantUuid, tenantUuid);
    const userPermissionsService: UserPermissionsService = createUserPermissionsService();
    const userPermissions: UserPermissions = await userPermissionsService.getUserPermissions();
    const storeModule: StoreModule = createStoreModule(
      pricingService,
      settingService,
      selfServiceModeService,
      companyService,
      contactService,
      consumerProfileService,
      deleteAccountService,
      userPermissions
    );
    this.store.registerModule('app', storeModule);
  }

  private unregisterStoreModule(): void {
    this.store.unregisterModule('app');
  }

  private async setLanguage(language: string): Promise<void> {
    const defaultLocale: string = 'en-US';
    const locale: string = getLocaleName(language);

    this.i18n.setLocaleMessage(defaultLocale, messages);
    await setupLocale(this.i18n, () => import(`@/app/locales/${locale}.json`), locale);
  }

  private async setBranding(branding: Branding): Promise<void> {
    await setupTheme(branding.color_scheme, this.element);
  }

  private async setEntry(entry: Entry): Promise<void> {
    switch (entry) {
      case Entry.Profile:
        await this.router.push('/profile');
        break;
      case Entry.Details:
        await this.router.push('/details');
        break;
      case Entry.ConsumerProfile:
        await this.router.push('/consumer-profile');
        break;
      case Entry.DeleteAccountConfirmation:
        await this.router.push('/delete-account-confirmation');
        break;
      case Entry.Wizard:
      default:
        await this.router.push('/');
        break;
    }
  }
}

export function createApp(
  router: Router,
  store: Store,
  i18n: I18n,
  analytics: AAnalyticsService,
  element?: HTMLElement
): App {
  return new App(
    router,
    store,
    i18n,
    analytics,
    element
  );
}
